import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Syringe, ShieldPlus, HeartPulse, Bandage, FlaskConical,
  Sparkles, Ear, Leaf, Activity, Stethoscope, Wind, Droplets, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  syringe: Syringe, shield: ShieldPlus, heart: HeartPulse, bandage: Bandage,
  flask: FlaskConical, sparkles: Sparkles, ear: Ear, leaf: Leaf,
  activity: Activity, stethoscope: Stethoscope, wind: Wind, droplets: Droplets,
};

const DEFAULT_TILES = [
  { id: "vaccinations", title: "Vaccinations", desc: "Flu, COVID-19, whooping cough and more — walk in or book.", icon: "syringe" },
  { id: "uti", title: "UTI Consults", desc: "Fast, private assessment and treatment advice for uncomplicated UTIs.", icon: "shield" },
  { id: "womens-health", title: "Women's Health", desc: "Confidential consults tailored to every stage of life.", icon: "heart" },
  { id: "woundcare", title: "Woundcare & Skin", desc: "Dressings, scar care and skin recovery plans with our pharmacists.", icon: "bandage" },
  { id: "compounding", title: "Compounding", desc: "Custom-made medicines prepared in our own compounding room — the birthplace of DermaGen.", icon: "flask" },
  { id: "beauty", title: "Beauty & Make-up", desc: "Personalised beauty advice and make-up services in store.", icon: "sparkles" },
  { id: "ear-piercing", title: "Ear Piercing", desc: "Safe, hygienic ear piercing with quality studs and aftercare guidance.", icon: "ear" },
  { id: "naturopath", title: "Naturopath Consults", desc: "Holistic health support blending natural and conventional care.", icon: "leaf" },
  { id: "pain-diabetic", title: "Pain & Diabetic Consults", desc: "Medication reviews, blood glucose guidance and pain management support.", icon: "activity" },
  { id: "health-checks", title: "Health Checks", desc: "Blood pressure, ear health and general wellness checks in store.", icon: "stethoscope" },
];

export default function Services() {
  const [tiles, setTiles] = useState(DEFAULT_TILES);

  useEffect(() => {
    fetch(`${API}/content/services`)
      .then((r) => r.json())
      .then((d) => { if (d.items?.length) setTiles(d.items); })
      .catch(() => {});
  }, []);

  return (
    <section id="services" data-testid="services-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 lg:px-10">
      <div className="mb-14 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink" data-testid="services-eyebrow">Pharmacy Services</p>
        <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="services-heading">
          More than a prescription counter
        </h2>
        <p className="mt-4 text-base text-muted-foreground md:text-lg">
          Our pharmacists and naturopath offer a full range of in-pharmacy services — most available by booking or walk-in.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6" data-testid="services-grid">
        {tiles.map((s, i) => {
          const Icon = ICONS[s.icon] || Sparkles;
          return (
            <motion.a
              key={s.id}
              href="#book"
              data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              className="group flex flex-col justify-between rounded-2xl border border-border/60 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(229,0,126,0.1)] lg:col-span-2"
            >
              <div>
                <div className="mb-5 inline-flex rounded-xl bg-brand-blush p-3 text-brand-pink transition-colors duration-200 group-hover:bg-brand-pink group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-heading text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-pink opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                Book this service <ArrowRight className="h-4 w-4" />
              </span>
            </motion.a>
          );
        })}
      </div>
    </section>
  );
}
